import { create } from 'zustand';
import {
  deleteGeminiApiKey,
  deleteOpenRouterApiKey,
  getAiAccessStatus,
  getCallableErrorMessage,
  setAiProviderConfig,
  setGeminiApiKey,
  setOpenRouterApiKey,
  type AiProvider,
  type AiUsage,
  type OpenRouterModel,
} from '../services/ai-functions';

interface AiAccessState {
  provider: AiProvider;

  geminiHasKey: boolean;
  geminiMaskedKey: string | null;

  openrouterHasKey: boolean;
  openrouterMaskedKey: string | null;
  openrouterModel: OpenRouterModel;

  // Convenience fields for existing UI (active provider).
  hasKey: boolean;
  maskedKey: string | null;

  dailyLimit: number;
  usedToday: number;
  remainingToday: number;
  tokenInput: number;
  tokenOutput: number;
  tokenTotal: number;

  loading: boolean;
  saving: boolean;
  deleting: boolean;
  error: string | null;

  refreshStatus: () => Promise<void>;
  setProvider: (provider: AiProvider) => Promise<void>;
  setOpenRouterModel: (model: OpenRouterModel) => Promise<void>;

  saveGeminiKey: (apiKey: string) => Promise<void>;
  removeGeminiKey: () => Promise<void>;

  saveOpenRouterKey: (apiKey: string) => Promise<void>;
  removeOpenRouterKey: () => Promise<void>;

  setUsage: (usage: AiUsage | { mode: 'tokens'; inputTokens: number; outputTokens: number; totalTokens: number } | ({ mode: 'prompts' } & AiUsage)) => void;
  clearError: () => void;
  reset: () => void;
}

const defaultState = {
  provider: 'gemini' as AiProvider,
  geminiHasKey: false,
  geminiMaskedKey: null as string | null,
  openrouterHasKey: false,
  openrouterMaskedKey: null as string | null,
  openrouterModel: 'gpt-5-nano-2025-08-07' as OpenRouterModel,
  hasKey: false,
  maskedKey: null as string | null,
  dailyLimit: 20,
  usedToday: 0,
  remainingToday: 20,
  tokenInput: 0,
  tokenOutput: 0,
  tokenTotal: 0,
  loading: false,
  saving: false,
  deleting: false,
  error: null as string | null,
};

function deriveActive(status: {
  provider: AiProvider;
  geminiHasKey: boolean;
  geminiMaskedKey: string | null;
  openrouterHasKey: boolean;
  openrouterMaskedKey: string | null;
}) {
  if (status.provider === 'openrouter') {
    return { hasKey: status.openrouterHasKey, maskedKey: status.openrouterMaskedKey };
  }
  return { hasKey: status.geminiHasKey, maskedKey: status.geminiMaskedKey };
}

export const useAiAccessStore = create<AiAccessState>()((set, get) => ({
  ...defaultState,

  refreshStatus: async () => {
    set({ loading: true, error: null });
    try {
      const status = await getAiAccessStatus();
      const next = {
        provider: status.provider,
        geminiHasKey: status.gemini.hasKey,
        geminiMaskedKey: status.gemini.maskedKey,
        openrouterHasKey: status.openrouter.hasKey,
        openrouterMaskedKey: status.openrouter.maskedKey,
        openrouterModel: status.openrouter.model,
        dailyLimit: status.dailyLimit,
        usedToday: status.usedToday,
        remainingToday: status.remainingToday,
      };
      set({ ...next, ...deriveActive(next), loading: false, error: null });
    } catch (error) {
      set({ loading: false, error: getCallableErrorMessage(error) });
    }
  },

  setProvider: async (provider) => {
    set({ saving: true, error: null });
    try {
      const status = await setAiProviderConfig({ provider });
      const next = {
        provider: status.provider,
        geminiHasKey: status.gemini.hasKey,
        geminiMaskedKey: status.gemini.maskedKey,
        openrouterHasKey: status.openrouter.hasKey,
        openrouterMaskedKey: status.openrouter.maskedKey,
        openrouterModel: status.openrouter.model,
        dailyLimit: status.dailyLimit,
        usedToday: status.usedToday,
        remainingToday: status.remainingToday,
      };
      set({ ...next, ...deriveActive(next), saving: false, error: null });
    } catch (error) {
      set({ saving: false, error: getCallableErrorMessage(error) });
      throw error;
    }
  },

  setOpenRouterModel: async (model) => {
    const provider: AiProvider = 'openrouter';
    set({ saving: true, error: null });
    try {
      const status = await setAiProviderConfig({ provider, openrouterModel: model });
      const next = {
        provider: status.provider,
        geminiHasKey: status.gemini.hasKey,
        geminiMaskedKey: status.gemini.maskedKey,
        openrouterHasKey: status.openrouter.hasKey,
        openrouterMaskedKey: status.openrouter.maskedKey,
        openrouterModel: status.openrouter.model,
        dailyLimit: status.dailyLimit,
        usedToday: status.usedToday,
        remainingToday: status.remainingToday,
      };
      set({ ...next, ...deriveActive(next), saving: false, error: null });
    } catch (error) {
      set({ saving: false, error: getCallableErrorMessage(error) });
      throw error;
    }
  },

  saveGeminiKey: async (apiKey: string) => {
    const normalized = apiKey.trim();
    if (!normalized) {
      set({ error: 'Enter a Gemini API key.' });
      return;
    }

    set({ saving: true, error: null });
    try {
      const status = await setGeminiApiKey(normalized);
      const next = {
        provider: status.provider,
        geminiHasKey: status.gemini.hasKey,
        geminiMaskedKey: status.gemini.maskedKey,
        openrouterHasKey: status.openrouter.hasKey,
        openrouterMaskedKey: status.openrouter.maskedKey,
        openrouterModel: status.openrouter.model,
        dailyLimit: status.dailyLimit,
        usedToday: status.usedToday,
        remainingToday: status.remainingToday,
      };
      set({ ...next, ...deriveActive(next), saving: false, error: null });
    } catch (error) {
      set({ saving: false, error: getCallableErrorMessage(error) });
      throw error;
    }
  },

  removeGeminiKey: async () => {
    set({ deleting: true, error: null });
    try {
      const status = await deleteGeminiApiKey();
      const next = {
        provider: status.provider,
        geminiHasKey: status.gemini.hasKey,
        geminiMaskedKey: status.gemini.maskedKey,
        openrouterHasKey: status.openrouter.hasKey,
        openrouterMaskedKey: status.openrouter.maskedKey,
        openrouterModel: status.openrouter.model,
        dailyLimit: status.dailyLimit,
        usedToday: status.usedToday,
        remainingToday: status.remainingToday,
      };
      set({ ...next, ...deriveActive(next), deleting: false, error: null });
    } catch (error) {
      set({ deleting: false, error: getCallableErrorMessage(error) });
      throw error;
    }
  },

  saveOpenRouterKey: async (apiKey: string) => {
    const normalized = apiKey.trim();
    if (!normalized) {
      set({ error: 'Enter an OpenRouter API key.' });
      return;
    }

    set({ saving: true, error: null });
    try {
      const status = await setOpenRouterApiKey(normalized);
      const next = {
        provider: status.provider,
        geminiHasKey: status.gemini.hasKey,
        geminiMaskedKey: status.gemini.maskedKey,
        openrouterHasKey: status.openrouter.hasKey,
        openrouterMaskedKey: status.openrouter.maskedKey,
        openrouterModel: status.openrouter.model,
        dailyLimit: status.dailyLimit,
        usedToday: status.usedToday,
        remainingToday: status.remainingToday,
      };
      set({ ...next, ...deriveActive(next), saving: false, error: null });
    } catch (error) {
      set({ saving: false, error: getCallableErrorMessage(error) });
      throw error;
    }
  },

  removeOpenRouterKey: async () => {
    set({ deleting: true, error: null });
    try {
      const status = await deleteOpenRouterApiKey();
      const next = {
        provider: status.provider,
        geminiHasKey: status.gemini.hasKey,
        geminiMaskedKey: status.gemini.maskedKey,
        openrouterHasKey: status.openrouter.hasKey,
        openrouterMaskedKey: status.openrouter.maskedKey,
        openrouterModel: status.openrouter.model,
        dailyLimit: status.dailyLimit,
        usedToday: status.usedToday,
        remainingToday: status.remainingToday,
      };
      set({ ...next, ...deriveActive(next), deleting: false, error: null });
    } catch (error) {
      set({ deleting: false, error: getCallableErrorMessage(error) });
      throw error;
    }
  },

  setUsage: (usage) => {
    const provider = get().provider;
    if ((usage as any)?.mode === 'tokens' || provider === 'openrouter') {
      const u = usage as any;
      set({
        tokenInput: typeof u.inputTokens === 'number' ? u.inputTokens : 0,
        tokenOutput: typeof u.outputTokens === 'number' ? u.outputTokens : 0,
        tokenTotal: typeof u.totalTokens === 'number' ? u.totalTokens : 0,
      });
      return;
    }
    const u = usage as AiUsage;
    set({
      dailyLimit: u.dailyLimit,
      usedToday: u.usedToday,
      remainingToday: u.remainingToday,
    });
  },

  clearError: () => set({ error: null }),
  reset: () => set({ ...defaultState }),
}));
